import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Plus, Trash2, Search } from "lucide-react";
import api from "@/lib/api";

const FIELD_LABELS = {
  name: "School name", city: "City", state: "State",
  contact_email: "Contact email", contact_phone: "Contact phone", teacher_count: "Teacher count",
};

const inputCls = "w-full h-12 px-4 rounded-2xl border outline-none text-sm bg-white transition-all";
const inputStyle = { borderColor: "#E2E8F0", color: "#1A202C" };
const focusHandlers = {
  onFocus: (e) => { e.target.style.borderColor = "#3B68E7"; e.target.style.boxShadow = "0 0 0 4px #3B68E71A"; },
  onBlur: (e) => { e.target.style.borderColor = "#E2E8F0"; e.target.style.boxShadow = "none"; },
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-semibold mb-1.5 block" style={{ color: "#64748B" }}>{label}</span>
    {children}
  </label>
);

export default function Schools() {
  const [schools, setSchools] = useState([]);
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", city: "", state: "", contact_email: "", contact_phone: "", teacher_count: 0 });
  const load = () => api.get("/schools").then((r) => setSchools(r.data));
  useEffect(() => { load(); }, []);
  const submit = async () => {
    try { await api.post("/schools", { ...form, teacher_count: parseInt(form.teacher_count) || 0 }); toast.success("School added"); setShowForm(false); setForm({ name: "", city: "", state: "", contact_email: "", contact_phone: "", teacher_count: 0 }); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const del = async (id) => { if (!confirm("Delete this school?")) return; await api.delete(`/schools/${id}`); toast.success("Deleted"); load(); };
  const filtered = schools.filter((s) => s.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="fade-up">
      <Header title="Schools" subtitle="Manage partner schools." actions={
        <button
          onClick={() => setShowForm(true)} data-testid="add-school"
          className="inline-flex items-center justify-center gap-1.5 text-white text-sm font-semibold px-5 rounded-full transition-transform hover:scale-[1.02]"
          style={{ backgroundColor: "#3B68E7", height: 44 }}
        >
          <Plus className="w-4 h-4" /> Add school
        </button>
      } />
      <div className="mt-6 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: "#94A3B8" }} />
          <input
            value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search schools…"
            className="w-full h-11 pl-10 pr-4 rounded-full border outline-none text-sm bg-white"
            style={{ borderColor: "#E2E8F0" }}
            {...focusHandlers}
          />
        </div>
      </div>

      <div className="mt-6 rounded-[24px] border bg-white overflow-hidden" style={{ borderColor: "#E2E8F0", boxShadow: "0 4px 20px rgba(15,23,42,0.04)" }}>
        {filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-sm" style={{ color: "#64748B" }}>{schools.length === 0 ? "No schools yet — add your first partner school." : "No schools match your search."}</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="sticky top-0 z-10" style={{ backgroundColor: "#F8FAFC" }}>
                <tr>
                  <th className="text-left px-5 py-3.5 font-semibold text-xs uppercase tracking-wider" style={{ color: "#64748B" }}>Name</th>
                  <th className="text-left px-5 py-3.5 font-semibold text-xs uppercase tracking-wider" style={{ color: "#64748B" }}>City</th>
                  <th className="text-left px-5 py-3.5 font-semibold text-xs uppercase tracking-wider" style={{ color: "#64748B" }}>Contact</th>
                  <th className="text-left px-5 py-3.5 font-semibold text-xs uppercase tracking-wider" style={{ color: "#64748B" }}>Teachers</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.id} className="border-t transition-colors hover:bg-[#F8FAFC]" style={{ borderColor: "#F1F5F9" }}>
                    <td className="px-5 py-3.5 font-medium" style={{ color: "#1A202C" }}>{s.name}</td>
                    <td className="px-5 py-3.5" style={{ color: "#64748B" }}>{s.city}, {s.state}</td>
                    <td className="px-5 py-3.5" style={{ color: "#64748B" }}>{s.contact_email}</td>
                    <td className="px-5 py-3.5" style={{ color: "#1A202C" }}>{s.teacher_count}</td>
                    <td className="px-5 py-3.5 text-right">
                      <button
                        onClick={() => del(s.id)} data-testid={`del-school-${s.id}`}
                        className="w-11 h-11 -my-2.5 grid place-items-center rounded-full transition-colors"
                        style={{ color: "#94A3B8" }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = "#DC2626"; e.currentTarget.style.backgroundColor = "#FEF2F2"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = "#94A3B8"; e.currentTarget.style.backgroundColor = "transparent"; }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && createPortal(
        <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div
            className="bg-white w-full max-w-lg fade-up overflow-y-auto"
            style={{ borderRadius: 24, maxHeight: "90vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 sm:p-8">
              <h2 className="rebrand-serif font-semibold mb-6" style={{ fontSize: "clamp(1.25rem, 1rem + 1vw, 1.5rem)", color: "#1A202C" }}>New school</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {["name", "city", "state", "contact_email", "contact_phone", "teacher_count"].map((k) => (
                  <Field key={k} label={FIELD_LABELS[k]}>
                    <input
                      placeholder={FIELD_LABELS[k]} value={form[k]}
                      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      data-testid={`school-${k}`}
                      className={inputCls} style={inputStyle} {...focusHandlers}
                    />
                  </Field>
                ))}
              </div>
              <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                <button
                  onClick={() => setShowForm(false)}
                  className="h-12 px-5 rounded-full text-sm font-medium border w-full sm:w-auto"
                  style={{ borderColor: "#E2E8F0", color: "#64748B" }}
                >
                  Cancel
                </button>
                <button
                  onClick={submit} data-testid="school-save"
                  className="h-12 px-6 rounded-full text-white text-sm font-semibold w-full sm:w-auto transition-transform hover:scale-[1.02]"
                  style={{ backgroundColor: "#3B68E7" }}
                >
                  Save school
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export const Header = ({ title, subtitle, actions }) => (
  <div className="flex flex-wrap items-end justify-between gap-4">
    <div>
      <h1 className="rebrand-serif font-semibold tracking-tight" style={{ fontSize: "clamp(1.5rem, 1.1rem + 1.2vw, 2rem)", color: "#1A202C" }}>{title}</h1>
      {subtitle && <p className="text-sm mt-1" style={{ color: "#64748B" }}>{subtitle}</p>}
    </div>
    <div>{actions}</div>
  </div>
);
